<template>
  <div>
    <main-layout
      controlsBg="light"
      :hasControlsBar="false"
      :breadcrumb="getBreadcrumb">
      <div class="container">
        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <div id="header">
              <h1 class="header-title">
                <chevron @click="$router.back()"/>
                {{ pageTitle }}
              </h1>
              <div class="header-add-button">
                <span
                  v-if="mode === 'mine' || mode === 'favorites'"
                  v-tooltip.left.top-start="$t('health.nutritionalPlan.addRecipe')"
                  @click="createRecipe">
                  <font-awesome-icon
                    class="header-add-button-icon"
                    :icon="icon.add"/>
                </span>
              </div>
            </div>
          </grid-col>
        </grid-row>

        <grid-row class="margin-top-xl" column-align="center">
          <grid-col col-size="4">
            <table-component
              ref="recipeTable"
              :data="fetchData"
              sort-by="name"
              sort-order="asc"
              :show-caption="false"
              class="datatable"
              filter-placeholder=""
              filterNoResults="">
              <table-column
                show="id"
                :hidden="true"
                :filterable="false"
                data-type="boolean">
              </table-column>
              <table-column
                show="name"
                :label="$t('health.patient.name')"
                header-class="text-left"
                cell-class="table-component__text-left">
              </table-column>

              <table-column
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click.stop="toggleFavorite(row)"
                    :icon="row.liked ? icon.heartSolid : icon.heart"
                    :class="['table-iconset-icon', { 'liked-icon': row.liked }]"/>
                </template>
              </table-column>

              <table-column
                v-if="mode === 'mine'"
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="editRecipe(row)"
                    :icon="icon.edit"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
              <table-column
                v-if="mode === 'mine'"
                label=""
                :sortable="false"
                :filterable="false"
                header-class="table-component__minimum__width__column"
                cell-class="text-center">
                <template slot-scope="row">
                  <font-awesome-icon
                    v-on:click="removeRecipe(row)"
                    :icon="icon.remove"
                    class="table-iconset-icon"/>
                </template>
              </table-column>
            </table-component>

            <div v-if="recipes.length === 0" class="no-data margin-top-l">
              {{ $t('health.recipe.empty') }}
            </div>
          </grid-col>
        </grid-row>
      </div>

      <ok-cancel-modal
        v-if="showConfirmationDeleteModal"
        v-on:ok="onDeleteConfirm"
        v-on:cancel="onDeleteCancel"
        :title="deleteModalMessage"
        :message="deleteModalMessage"
        :okButton="$t('commons.delete')"
        :cancelButton="$t('commons.cancel')">
        <div slot="body">
          <ul>
            <li>
              {{ recipeToDelete.name }}
            </li>
          </ul>
        </div>
      </ok-cancel-modal>

    </main-layout>
  </div>
</template>

<script>
import MainLayout from '@/layouts/MainLayout';
import GridRow from '@/components/shared/grid/GridRow';
import GridCol from '@/components/shared/grid/GridCol';
import TableComponent from '@/components/shared/table/components/TableComponent';
import TableColumn from '@/components/shared/table/components/TableColumn';
import pagination from '@/components/shared/table/pagination';
import Chevron from '@/components/shared/Chevron';
import OkCancelModal from '@/components/shared/dialogs/OkCancelModal';
import FontAwesomeIcon from '@fortawesome/vue-fontawesome';
import {faPencilAlt, faTrashAlt, faHeart} from '@fortawesome/pro-regular-svg-icons';
import {faPlusCircle, faHeart as faHeartSolid} from '@fortawesome/pro-solid-svg-icons';
import api from 'BRAND/api/recipe';
import {mapGetters} from 'vuex';

const VALID_MODES = ['glucovibes', 'mine', 'favorites'];

export default {
  name: 'RecipeListView',
  components: {
    MainLayout,
    GridRow,
    GridCol,
    TableComponent,
    TableColumn,
    FontAwesomeIcon,
    Chevron,
    OkCancelModal
  },
  props: {
    mode: {
      type: String,
      required: true,
      validator: (value) => VALID_MODES.includes(value)
    }
  },
  data () {
    return {
      recipes: [],
      icon: {
        add: faPlusCircle,
        remove: faTrashAlt,
        edit: faPencilAlt,
        heart: faHeart,
        heartSolid: faHeartSolid
      },
      showConfirmationDeleteModal: false,
      recipeToDelete: undefined,
      deleteModalMessage: '',
      tablePageSize: 10,
      tableState: {
        filter: '',
        page: 1,
        sort: {
          order: '',
          fieldName: ''
        }
      }
    };
  },
  mounted () {
    this.requestRecipes();
  },
  computed: {
    ...mapGetters([
      'getLanguageCode',
      'getUserAccount'
    ]),
    pageTitle () {
      return this.$t(`health.recipe.tabs.${this.mode}`);
    },
    getBreadcrumb () {
      return [
        {link: '/workspace', name: this.$t('menu.workspace')},
        {link: '/recipe', name: this.$t('health.recipe.title')},
        {link: this.$route.path, name: this.pageTitle, disabled: true}
      ];
    },
    getRecipes () {
      return this.recipes;
    }
  },
  methods: {
    fetchData ({page, filter, sort}) {
      const tablePage = pagination.createPage(
        this.getRecipes,
        this.tableState,
        ['name'],
        this.tablePageSize,
        {page, filter, sort});
      this.tableState = tablePage.state;
      return tablePage.result;
    },
    requestRecipes () {
      const languageCode = this.getLanguageCode;
      this.apiCallForMode(languageCode)
        .then((response) => {
          this.recipes = response;
          if (this.$refs.recipeTable) {
            this.$refs.recipeTable.refresh();
          }
        })
        .catch((error) => console.error('Failed to load recipes', error));
    },
    apiCallForMode (languageCode) {
      switch (this.mode) {
        case 'mine':
          return api.getRecipesByNutritionist(this, languageCode);
        case 'favorites':
          return api.getFavoriteRecipes(this, languageCode);
        case 'glucovibes':
        default:
          return api.getRecipes(this, languageCode);
      }
    },
    toggleFavorite (row) {
      const action = row.liked
        ? api.removeFavorite(this, row.id)
        : api.addFavorite(this, row.id);

      action
        .then(() => {
          if (this.mode === 'favorites') {
            this.requestRecipes();
          } else {
            this.$set(row, 'liked', !row.liked);
          }
        })
        .catch((error) => console.error('Failed to toggle favorite', error));
    },
    onDeleteConfirm () {
      api.removeRecipe(this, this.recipeToDelete.id)
        .then(() => {
          this.requestRecipes();
          this.showConfirmationDeleteModal = false;
        })
        .catch((error) => console.error('Failed to delete recipe', error));
    },
    onDeleteCancel () {
      this.showConfirmationDeleteModal = false;
      this.recipeToDelete = undefined;
      this.deleteModalMessage = '';
    },
    createRecipe () {
      this.$router.push({name: 'EditRecipe', params: {mode: 'create', user: this.getUserAccount}});
    },
    editRecipe (row) {
      this.$router.push({name: 'EditRecipe', params: {id: row.id, mode: 'edit', user: this.getUserAccount}});
    },
    removeRecipe (row) {
      this.recipeToDelete = row;
      this.deleteModalMessage = this.$t('health.recipe.delete');
      this.showConfirmationDeleteModal = true;
    }
  }
};
</script>

<style lang="scss">
@import "src/stylesheets/variables";

#header {
  display: inline-block;
  width: 100%;
  line-height: 1rem;
}
.header-title {
  float: left;
}
.header-add-button {
  float: right;
}
.header-add-button > span + span {
  margin-left: 1rem;
}
.header-add-button-icon {
  font-size: 3rem;
  color: $secondary;
  cursor: pointer;
}

.liked-icon {
  color: #e53e3e;
}

.no-data {
  text-align: center;
  color: $black-light4;
  font-style: italic;
}
</style>



// WEBPACK FOOTER //
// src/brand/health/layouts/recipe/RecipeListView.vue